import React, { Fragment, useContext } from 'react'
import PropTypes from 'prop-types'
import { MessageFormat } from '@paypalcorp/worldready'
import {
  Message,
  Context as WorldReadyContext,
} from '@paypalcorp/worldready-react'
import { useQuery } from '@apollo/client'
import { Redirect } from 'react-router-dom'

import getHomepageData from '../../graphql/getHomepageData'
import getAllProducts from '../../graphql/getAllProducts'
import { Box } from '../../components/box'
import { BundleCardWithData } from '../../components/bundle-card'
import { ByobCallout } from '../../components/byob-callout'
import { Carousel } from '../../components/carousel'
import { Container, Row, Col } from '../../components/grid'
import { GiftCard } from '../../components/gift-card'
import { GuideCard } from '../../components/guide-card'
import { HeroHeader } from '../../components/hero-header'
import { Loader } from '../../components/loader'
import { LogoBubble } from '../../components/logo-bubble'
import { PromoCard } from '../../components/promo-card'
import { VerticalGiftListWithData } from '../../components/vertical-gift-list'
import { TitleTicker } from '../../components/title-ticker'
import { useTrackImpression } from '../../utils/hooks/useTrackImpression'
import { handleSessionError } from '../../utils/error-tracking'
import { filterBundles, filterCards } from '../../utils/cardFilters'
import clientData from '../../utils/client-data'
import { HomePageSection } from './HomePageSection'
import { giftGuides } from '../../gift-guide-routes'
import brands from './HomePageBrands'
import { Text } from '../../components/text'

const elmo = clientData.experimentation
const country = clientData.locality.country

const heroMedia = {
  lg: 'https://paypalobjects.com/gifts/assets/holiday/holiday_home_hero_2024_lg.jpg',
  sm: 'https://paypalobjects.com/gifts/assets/holiday/holiday_home_hero_2024_sm.jpg',
}

const HomePageContent = ({ pageData }) => {
  const { giftCards, bundles, guides, lists, promos } = pageData

  const worldready = useContext(WorldReadyContext)
  const { data, loading, error } = useQuery(getHomepageData, {
    variables: { urlKey: 'categories/popular' },
  })
  // prefetch getAllProducts for caching
  useQuery(getAllProducts)
  useTrackImpression(
    'main:consumer:gifts:home:',
    'main:consumer:gifts:home::::',
  )

  if (loading) return <Loader py="100px" />

  if (error) {
    handleSessionError(error)
    return <Redirect to="/error" />
  }

  const popularGifts = data.getProductsInCategory.all_a_z
  const popularCards = popularGifts.filter(filterBundles)
  const popularBundles = popularGifts.filter(filterCards)
  const { deals } = data.getLandingData

  return (
    <Fragment>
      {popularCards && !!popularCards.length && (
        <HomePageSection section={giftCards}>
          {popularCards.map((card, i) => (
            <Col md={3} key={`popular-gift-card-${i}`} mb="x4">
              <GiftCard
                url={card.url_key}
                img={card.img_url}
                label={card.name}
                name="GiftCard"
                data-pagename="main:consumer:gifts:home:"
                data-pagename2={`main:consumer:gifts:home:card:${card.url_key}::`}
              />
            </Col>
          ))}
        </HomePageSection>
      )}
      {popularBundles &&
        !!popularBundles.length &&
        (elmo.v2bundles ? (
          <HomePageSection
            section={bundles}
            data-testid="popular-gift-card-section"
          >
            <Carousel
              items={popularBundles.map((bundle, i) => (
                <Box
                  height="100%"
                  width="300px"
                  key={`popular-bundle-box-${i}`}
                >
                  <BundleCardWithData
                    mini
                    urlKey={bundle.url_key}
                    name="BundleCard"
                    data-pagename="main:consumer:gifts:home:"
                    data-pagename2={`main:consumer:gifts:home:bundle:${bundle.url_key}::`}
                  />
                </Box>
              ))}
            />
          </HomePageSection>
        ) : null)}
      {elmo.v2bundles ? (
        <ByobCallout
          mt="2rem"
          title={new MessageFormat(worldready, {
            id: 'pages/home.section.byob.header',
          }).format()}
          eyebrow={<Message id="pages/home.section.byob.eyebrow" />}
          ctaLabel={<Message id="pages/home.section.byob.cta" />}
          ctaHref="/byob"
          name="BYOCTA"
          data-testid="byob-callout-section"
          data-pagename="main:consumer:gifts:home:"
          data-pagename2="main:consumer:gifts:home:byocta:::"
        />
      ) : null}
      {elmo.v2guides ? (
        <HomePageSection section={guides} data-testid="gift-guides-section">
          <Carousel
            items={guides.cards.map((card, i) => (
              <Box width="300px" key={`card-box-${i}`}>
                <GuideCard
                  card={card}
                  data-pagename="main:consumer:gifts:home:"
                  data-pagename2="main:consumer:gifts:home:guidecard:::"
                />
              </Box>
            ))}
          />
        </HomePageSection>
      ) : null}

      {country === 'US' && (
        <Container>
          <Row my="1" align="center">
            <Text
              as="h2"
              color="secondary.greydark"
              dangerouslySetInnerHTML={{
                __html: new MessageFormat(worldready, {
                  id: 'pages/home.disclaimer.scams',
                }).format(),
              }}
            />
          </Row>
        </Container>
      )}

      <HomePageSection section={brands} data-testid="top-brands-section">
        <Carousel
          items={brands.cards.map((logo, i) => (
            <Box width="150px" height="150px" key={`brand-box-${i}`}>
              <LogoBubble
                alt={logo.name}
                imgUrl={logo.img_url}
                url={`/${logo.url_key}`}
                name="TopBrand"
                data-pagename="main:consumer:gifts:home:"
                data-pagename2={`main:consumer:gifts:home:topbrand:${logo.url_key
                  .toLowerCase()
                  .replace(/[^A-Za-z]/g, '-')}::`}
              />
            </Box>
          ))}
        />
      </HomePageSection>

      {deals && !!deals.length ? (
        <HomePageSection data-testid="deals-section" section={promos} noEyebrow>
          <Carousel
            items={deals.map((deal, i) => (
              <PromoCard
                key={i}
                title={deal.name}
                smImg={deal.mobile_banner_url}
                lgImg={deal.banner_url || deal.img_url}
                href={deal.url_key}
                terms={deal.terms}
                name="SeeDeal"
                data-pagename="main:consumer:gifts:home:"
                data-pagename2={`main:consumer:gifts:home:deal:${deal.url_key}::`}
                isMarketingPromo={deal.is_marketing_promotion}
              />
            ))}
          />
        </HomePageSection>
      ) : null}

      <Container as="section" data-testid="vertical-lists-section">
        <Row>
          <Col md={12} lg={6}>
            <VerticalGiftListWithData
              name={lists.cards.name}
              category={lists.cards.category}
              cardStyle="cards"
              maxNumItems={5}
            />
          </Col>
          {elmo.v2bundles ? (
            <Col md={12} lg={6}>
              <VerticalGiftListWithData
                name={lists.bundles.name}
                category={lists.bundles.category}
                cardStyle="bundles"
                maxNumItems={5}
              />
            </Col>
          ) : (
            <Col md={12} lg={6}>
              <VerticalGiftListWithData
                name={lists.cards2.name}
                category={lists.cards2.category}
                cardStyle="cards"
                maxNumItems={5}
              />
            </Col>
          )}
        </Row>
      </Container>

      {country === 'GB' && (
        <Container>
          <Row my="x3">
            <Text as="h3" color="secondary.greydark">
              <Message id="pages/home.disclaimer.regulation" />
            </Text>
          </Row>
        </Container>
      )}
    </Fragment>
  )
}

const HomePage = () => {
  const worldready = useContext(WorldReadyContext)
  const textItems = new MessageFormat(worldready, {
    id: 'pages/home.hero.title.items',
  }).format()
  const suggestions = new MessageFormat(worldready, {
    id: 'pages/suggestions.suggestions.all',
  }).format()

  return (
    <Fragment>
      <HeroHeader
        headerStyle="search"
        headlineComponent={
          <TitleTicker textItems={textItems.split('|')} delay={4000} />
        }
        searchSuggestions={suggestions ? suggestions.split(',') : []}
        mediaSrc={heroMedia}
        isHeroExpanded
      />
      <HomePageContent />
    </Fragment>
  )
}

HomePageContent.propTypes = {
  pageData: PropTypes.object,
}

HomePageContent.defaultProps = {
  pageData: {
    giftCards: {
      name: 'cards',
      ctaHref: '/all',
    },
    bundles: {
      name: 'bundles',
      ctaHref: '/bundles',
    },
    guides: {
      name: 'guides',
      ctaHref: '/gift-guides',
      cards: giftGuides,
    },
    lists: {
      cards: {
        name: 'cardsList',
        category: {
          US: 'categories/travel-and-leisure',
          GB: 'categories/gaming',
          CA: 'categories/dining-and-food',
          AU: 'occasions/just-because',
          DE: 'occasions/jahrestag',
          AT: 'categories/shopping',
        }[country],
      },
      cards2: {
        name: 'bundlesList',
        category: {
          US: 'categories/sports-and-outdoor',
          GB: 'occasions/birthday',
          CA: 'categories/gifts-and-variety',
          AU: 'occasions/birthday',
          DE: 'occasions/geburtstag',
          AT: 'categories/entertainment',
        }[country],
      },
      bundles: {
        name: 'bundlesList',
        category: {
          US: 'categories/sports-and-outdoor',
          GB: 'occasions/birthday',
          CA: 'occasions/holiday',
          AU: 'occasions/birthday',
          DE: 'occasions/geburtstag',
          AT: 'occasions/geburtstag',
        }[country],
      },
    },
    promos: {
      name: 'deals',
    },
  },
}

export default HomePage
